import S, { DataSignal } from 's-js';
import { SynchronousSet } from 'lib/SynchronousSet';

type id = string | number;

export interface SynchronousIndex<T, R> {
    (id : id | (() => id)) : () => R
    add(item : T) : void;
    delete(item : T) : void;
}

export const SynchronousIndex = <T, S, R>(
    key : (v : T) => id | null, 
    empty : (id : id) => S, 
    add : (slot: S, item : T) => boolean, 
    del : (slot: S, item : T) => boolean, 
    read : (slot : S) => R,
    init? : () => Iterable<T>
) : SynchronousIndex<T, R> => {
    const
        table = ({}) as { [ id : string ] : { changed : DataSignal<boolean>, value: S } | undefined },
        disposers = new Map<T, () => void>(),
        index = ref as SynchronousIndex<T, R>;

    index.add = _add;
    index.delete = _delete;

    if (init) for (let i of Array.from(S.sample(init))) _add(i);

    S.cleanup(() => disposers.forEach(disposer => disposer()));

    return index;

    function slot(id : id) {
        let s = table[id];
        return s ? s : table[id] = { changed: S.data(true), value: empty(id) };
    }

    function ref(id : id | (() => id)) {
        return function index() { 
            const s = slot(id instanceof Function ? id() : id);
            s.changed();
            return read(s.value);
        };
    }

    function _add(item : T) {
        if (!disposers.has(item)) {
            S.root(dispose => {
                disposers.set(item, dispose);
                S<id | null>(oldId => {
                    const id = key(item);
                    if (id !== oldId) {
                        // Treat null as a special key value meaning "don't slot me."
                        if (oldId !== null) updateSlot(oldId, item, del);
                        if (id !== null) updateSlot(id, item, add);
                    }
                    return id;
                }, null);
            });
        }
    }

    function _delete(item : T) {
        const disposer = disposers.get(item);
        if (disposer) {
            disposers.delete(item);
            disposer();
            const id = key(item);
            if (id !== null) updateSlot(id, item, del);
        }
    }

    function updateSlot(id : id, item : T, fn : (cur: S, item : T) => boolean) {
        let s = slot(id);
        if (fn(s.value, item)) s.changed(true);
    }
}

export interface SynchronousUniqueIndex<T> {
    (id : id | (() => id)) : () => T | null
    add(item : T) : void;
    delete(item : T) : void;
}

export const SynchronousUniqueIndex = <T>(key : (v : T) => id | null, init? : () => Iterable<T>) : SynchronousUniqueIndex<T> => {
    type slot = { id : id, value : T | null, count : number };
    const
        changed = S.data(true),
        changes = [] as slot[];
    let changeCount = 0;
    S.root(() => S.on(changed, () => {
        let errors = "";
        for (let i = 0; i < changeCount; i++) {
            let s = changes[i];
            if (s.count > 1) {
                errors += "key: " + s.id + ", value: " + s.value + ". ";
                s.count = 1;
            }
        }
        changeCount = 0;
        if (errors) throw new Error("SynchronousUniqueIndex collision(s): " + errors);
    }));
    return SynchronousIndex<T, slot, T | null>(
        key,
        id => ({ id, value: null, count: 0 }),
        (s, i) => (s.value !== i    && (changed(true), changes[changeCount++] = s, s.value = i,    s.count++, true)),
        (s, i) => (s.value !== null && (changed(true), changes[changeCount++] = s, s.value = null, s.count--, true)),
        (s) => s.value,
        init
    )
}

export interface SynchronousNonuniqueIndex<T> {
    (id : id | (() => id)) : () => Set<T>
    add(item : T) : void;
    delete(item : T) : void;
}

export const SynchronousNonuniqueIndex = <T>(key : (v : T) => id | null, init? : () => Iterable<T>) : SynchronousNonuniqueIndex<T> =>
    SynchronousIndex<T, SynchronousSet<T>, Set<T>>(
        key,
        id => SynchronousSet<T>(),
        (s, i) => S.sample(s).has(i) ? false : (s.add(i), true),
        (s, i) => s.delete(i),
        s => s(),
        init
    );


// WEBPACK FOOTER //
// ./lib/SynchronousIndex.ts